import React, {useEffect, useRef, useState} from "react"
import PropTypes from "prop-types"
import {connect} from "react-redux"
import {withRouter, useLocation} from "react-router-dom"
import {Button, Card, CardBody, Col, Container, Form, Row} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import {registerLocale, setDefaultLocale} from "react-datepicker"
import ptBR from "date-fns/locale/pt-BR"
import AlertaSemVinculo from "../../components/Common/alerta-sem-vinculo"

import {getCompanies, getCompaniesValuesByOptions, getCompanyOption} from "../../helpers/utils"
import FormFilterCompany from "../../components/Common/form-filter-company"
import FormFilterText from "../../components/Common/form-filter-text"
import FormFilterSelect from "../../components/Common/form-filter-select"
import AreasArmazenamentoTable from "./areas-armazenamento-table"
import { navegarBreadCrumb } from "../../store/navigator/actions"
import usePermission from "helpers/permission/usePermission"

import { deleteAreaRequest, duplicateAreaRequest } from "../../store/areasArmazenamento/actions"
import { t } from "i18next"
import { getDisponibilidadeOptions, getTipoAreaOptions } from "./const"

registerLocale("ptBR", ptBR)
setDefaultLocale("ptBR")

const AreasArmazenamentoList = props => {
    const ref = useRef();

    const [filters, setFilters] = useState(null)

    const [companies, setCompanies] = useState([])
    const [companiesOption, setCompaniesOption] = useState([])
    const [nomeArea, setNomeArea] = useState("")
    const [tipoArea, setTipoArea] = useState("")
    const [disponivel, setDisponivel] = useState("")

    const usePermissionRemove = usePermission("areaEstoque_excluir")
    const usePermissionInsert = usePermission("areaEstoque_inserir")
    const usePermissionEdit = usePermission("areaEstoque_editar")

    const possuiVinculo = (props.user?.vinculos || []).some(vinculo => ["A", "T"].includes(vinculo.vinculo_status))
    const location = useLocation();

    useEffect(() => {
        if(props.dashboardFilters?.companiesSelected) setCompanies(props.dashboardFilters.companiesSelected)

        if (possuiVinculo) {
            populateFiltros(props.dashboardFilters?.companiesSelected)
        }
        props.navegarBreadCrumb({title: `breadcrumb.stock.storageArea.title`, link: location.pathname})
    }, [])

    useEffect(() => {
        if(props.user?.usuario_info && props.user?.usuario_info?.perfil?.administrador && Array.isArray(props.empresas)) {
            let companiesOptions = props.empresas.map(empresa => getCompanyOption(empresa));
            setCompaniesOption(companiesOptions)
        } else {
            let companiesOptions = getCompanies(props.user)
            setCompaniesOption(companiesOptions)
        }
        return () => {
            setCompaniesOption([])
        }
    }, [props.user, props.empresas])

    useEffect(() => {
        if(props.areaArmazenamentoRemovida || props.areaArmazenamentoDuplicada) {
            pesquisar()
        }
    }, [props.areaArmazenamentoRemovida, props.areaArmazenamentoDuplicada])

    const populateFiltros = (companiesSelected) => {
        const filters = []

        if (companiesSelected && companiesSelected.length > 0) {
            const vals = getCompaniesValuesByOptions(companiesSelected)
            if (Array.isArray(vals) && vals.length > 0) filters.push({ id: 'idEmpresa', value: vals })
        } else if (Array.isArray(companies) && companies.length > 0) {
            const vals = getCompaniesValuesByOptions(companies)
            if (Array.isArray(vals) && vals.length > 0) filters.push({ id: 'idEmpresa', value: vals })
        }

        if (nomeArea) filters.push({ id: 'nome_area', value: nomeArea, condition: 'contains' })
        if (tipoArea) filters.push({ id: 'tipo_area', value: tipoArea })
        if (disponivel) filters.push({ id: 'disponivel', value: disponivel })

        setFilters(filters)
    }

    const pesquisar = () => {
        populateFiltros()
        setTimeout(() => ref?.current?.search(), 300)
    }

    const goToForm = (item) => {
        props.history.push("/area-estoque" + (item ? `/${item.id}` : "/criar"))
    }

    const handleEdit = (item) => goToForm(item)

    const handleDuplicate = (item) => {
        props.duplicateAreaRequest(item?.id, () => pesquisar())
    }

    const handleRemove = (item) => {
        props.deleteAreaRequest(item?.id, () => pesquisar())
    }

    if (
        !props.user ||
        !(props.user.vinculos || []).filter(e => ["A", "T"].includes(e.vinculo_status)).length
    ) {
        return (
            <AlertaSemVinculo/>
        )
    }

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>

            <Breadcrumbs title="breadcrumb.stock.storageArea.title" />

            <Row>
              <Col lg="12">
                <div>
                  <Card>
                    <CardBody>
                      <h4 className="card-title mb-3">
                        {t("areas.armazenamento.title")}
                      </h4>

                      <Form>
                        <Row>
                          <div className="col-md-3 col-sm-12">
                            <FormFilterCompany
                              attribute="companies"
                              value={companies}
                              description={t("areas.armazenamento.filters.empresa")}
                              setValue={value => setCompanies(value)}
                              options={companiesOption}
                              multiple={true}
                              hasSelectAll={
                                !(
                                  props.user?.usuario_info &&
                                  props.user?.usuario_info?.perfil
                                    ?.administrador
                                )
                              }
                            />
                          </div>

                          <div className="col-md-3 col-sm-12">
                            <FormFilterText
                              attribute="nome_area"
                              value={nomeArea}
                              description={t("areas.armazenamento.filters.nomeArea")}
                              changeHandeler={value => setNomeArea(value)}
                            />
                          </div>

                          <div className="col-md-2 col-sm-12">
                            <FormFilterSelect
                              attribute="tipo_area"
                              value={tipoArea}
                              description={t("areas.armazenamento.form.tipoArea")}
                              changeHandeler={value => setTipoArea(value)}
                              options={getTipoAreaOptions(true)}
                            />
                          </div>

                          <div className="col-md-2 col-sm-12">
                            <FormFilterSelect
                              attribute="disponivel"
                              value={disponivel}
                              description={t("areas.armazenamento.filters.disponibilidade")}
                              changeHandeler={value => setDisponivel(value)}
                              options={getDisponibilidadeOptions()}
                            />
                          </div>

                          <div className="col col-auto align-self-start mt-md-4 pt-1 row">
                            <div className="mt-3 col col-auto">
                              <Button
                                color="primary"
                                className="w-md"
                                onClick={e => pesquisar()}
                              >
                                {t("areas.armazenamento.actions.search")}
                              </Button>
                            </div>

                            <div className="mt-3 col col-auto">
                              <Button
                                type="button"
                                color="success"
                                disabled={!usePermissionInsert}
                                onClick={e => goToForm(null)}
                              >
                                <i className="bx bx-plus" />{" "}
                                {t("areas.armazenamento.actions.new")}
                              </Button>
                            </div>
                          </div>
                        </Row>
                      </Form>
                    </CardBody>
                  </Card>

                  {filters && (
                    <Card>
                      <CardBody>
                        <AreasArmazenamentoTable
                          ref={ref}
                          filters={filters}
                          usePermissionRemove={usePermissionRemove}
                          usePermissionEdit={usePermissionEdit}
                          onEdit={handleEdit}
                          onDuplicate={handleDuplicate}
                          onRemove={handleRemove}
                        ></AreasArmazenamentoTable>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    )
}

AreasArmazenamentoList.propTypes = {
    areaArmazenamentoRemovida: PropTypes.any,
    deleteAreaRequest: PropTypes.func,
    duplicateAreaRequest: PropTypes.func,
}

const mapStatetoProps = state => {
    const { empresas, empresasLoading } = state.Profile || {}
    const { user, dashboardFilters } = state.Login || {}
    return { user, dashboardFilters, empresas, empresasLoading }
}

export default connect(mapStatetoProps, {
    deleteAreaRequest,
    duplicateAreaRequest,
    navegarBreadCrumb
})(withRouter(AreasArmazenamentoList))